import { useState } from 'react';
import { DEFAULT_CEREBRO_CONTROL_BASE, DEFAULT_CEREBRO_LOCAL_AUDIO_BASE, useAssistantStore } from '@/stores/useAssistantStore';
import { Settings, Server, Key, Sliders, RotateCcw, Save } from 'lucide-react';

export function SettingsPanel() {
  const { apiBase, localAudioBase, authToken, setApiBase, setLocalAudioBase, setAuthToken, clearMessages } = useAssistantStore();
  const [controlBase, setControlBase] = useState(apiBase);
  const [audioBase, setAudioBase] = useState(localAudioBase);
  const [localToken, setLocalToken] = useState(authToken);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    setApiBase(controlBase);
    setLocalAudioBase(audioBase);
    setAuthToken(localToken);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleReset = () => {
    setControlBase(DEFAULT_CEREBRO_CONTROL_BASE);
    setAudioBase(DEFAULT_CEREBRO_LOCAL_AUDIO_BASE);
    setLocalToken('');
    setApiBase(DEFAULT_CEREBRO_CONTROL_BASE);
    setLocalAudioBase(DEFAULT_CEREBRO_LOCAL_AUDIO_BASE);
    setAuthToken('');
  };

  return (
    <div className="bg-void/95 border border-border-subtle rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border-subtle bg-surface/30">
        <Settings size={14} className="text-tertiary" />
        <span className="text-label text-tertiary">SETTINGS</span>
      </div>

      <div className="p-3 space-y-4">
        {/* Durable control plane */}
        <div>
          <label className="flex items-center gap-1.5 text-label text-tertiary mb-1.5">
            <Server size={10} />
            CONTROL PLANE URL
          </label>
          <input
            type="text"
            value={controlBase}
            onChange={(e) => setControlBase(e.target.value)}
            className="w-full bg-surface border border-border-subtle rounded-lg px-3 py-2 text-sm text-primary focus:border-violet focus:outline-none"
            placeholder={DEFAULT_CEREBRO_CONTROL_BASE}
          />
        </div>

        {/* Local audio execution plane */}
        <div>
          <label className="flex items-center gap-1.5 text-label text-tertiary mb-1.5">
            <Server size={10} />
            LOCAL AUDIO OS URL
          </label>
          <input
            type="text"
            value={audioBase}
            onChange={(e) => setAudioBase(e.target.value)}
            className="w-full bg-surface border border-border-subtle rounded-lg px-3 py-2 text-sm text-primary focus:border-ice focus:outline-none"
            placeholder={DEFAULT_CEREBRO_LOCAL_AUDIO_BASE}
          />
        </div>

        {/* Auth Token */}
        <div>
          <label className="flex items-center gap-1.5 text-label text-tertiary mb-1.5">
            <Key size={10} />
            FIONA AUTH TOKEN
          </label>
          <input
            type="password"
            value={localToken}
            onChange={(e) => setLocalToken(e.target.value)}
            className="w-full bg-surface border border-border-subtle rounded-lg px-3 py-2 text-sm text-primary focus:border-violet focus:outline-none"
            placeholder="X-Ark-Auth token..."
          />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t border-border-subtle">
          <button
            onClick={handleSave}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-all ${
              saved
                ? 'bg-neon/15 text-neon border border-neon/30'
                : 'bg-violet/15 text-violet border border-violet/30 hover:bg-violet/25'
            }`}
          >
            <Save size={14} />
            {saved ? 'SAVED' : 'SAVE'}
          </button>
          <button
            onClick={handleReset}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-surface/50 text-secondary border border-border-subtle hover:bg-hover hover:text-primary transition-all"
          >
            <RotateCcw size={14} />
            RESET
          </button>
          <button
            onClick={clearMessages}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-fire/10 text-fire border border-fire/20 hover:bg-fire/20 transition-all"
          >
            <Sliders size={14} />
            CLEAR CHAT
          </button>
        </div>

        {/* Info */}
        <div className="text-[10px] text-tertiary space-y-1 pt-1">
          <p>Commands use the durable Cloudflare control plane. TTS, STT, DAW, and effects use the docked local Audio OS.</p>
          <p>The FIONA credential is sent only to the control plane; it is never sent to the local audio endpoint.</p>
          <p>Rate limit: 100 req/min, burst 20</p>
          <p>Control: /health, /v1/command. Local Audio OS: /health, /v1/tts, /v1/stt, /v1/daw, /v1/effects.</p>
        </div>
      </div>
    </div>
  );
}
